import { useMutation } from '@apollo/client';
import type { DeleteThirdPartyMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteThirdPartyDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useDeleteThirdPartyTRPC } from './useDeleteThirdPartyTRPC';

export const useDeleteThirdParty = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [deleteThirdPartyGraphQL, graphqlState] = useMutation(
    DeleteThirdPartyDocument,
    {
      update: (cache) => {
        evictField(cache, 'third_party');
        evictField(cache, 'third_party_by_pk');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useDeleteThirdPartyTRPC();

  const deleteThirdParty = async (
    id: string
  ): Promise<DeleteThirdPartyMutation> => {
    if (trpcEnabled) {
      return trpcMutation.deleteThirdParty(id);
    }

    const result = await deleteThirdPartyGraphQL({
      variables: { Id: id },
    });
    if (!result.data) {
      throw new Error('Failed to delete third party');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      deleteThirdParty,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteThirdParty,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
